/**
 * Naming a security the way a person would.
 *
 * A proposal's execution legs carry `fundName`, but the backend leaves it null
 * often enough that falling straight back to the symbol showed the desk rows
 * like "INF090I01239" where a fund name belongs. The same payload usually DOES
 * know the name — the plan's candidates carry `name` against `symbol` — so the
 * fallback is a lookup, not a shrug.
 *
 * Order: whatever the row itself says, then the plan's own index, then the
 * symbol. The symbol is the last resort and never the first answer.
 */

/** symbol → name, from everything in a plan that knows one. */
export function nameIndex(plan) {
  const index = new Map();
  const add = (symbol, name) => {
    if (!symbol || !name) return;
    if (String(name) === String(symbol)) return;
    if (!index.has(symbol)) index.set(symbol, name);
  };
  (plan?.candidates ?? []).forEach((c) => add(c.symbol, c.name));
  (plan?.executionPlan?.executionLegs ?? []).forEach((l) => add(l.holdingSecuritySymbol, l.fundName));
  return index;
}

/** The name to show, falling back to the symbol only when nothing knows one. */
export const nameFor = (symbol, index, own = null) => {
  if (own && String(own) !== String(symbol)) return own;
  return index?.get(symbol) || own || symbol || '—';
};

/**
 * The symbol, when it is worth repeating.
 *
 * Under a name it is an identifier worth having; under itself it is the same
 * string twice. Callers render the second line only when this returns one —
 * which is what "the symbol after the name, where the context needs it" means.
 */
export const symbolNote = (symbol, shownName) => (
  symbol && String(symbol) !== String(shownName) ? symbol : null
);
